import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { RankingCategory } from './RankingCategory';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class RaceContestant extends BaseObj {
    public ID: number;
    public __id: number;

    public AlternativeBib: number | null;
    public __alternativebib: number | null;

    public AlternativeChip1: string | null;
    public __alternativechip1: string | null;

    public AlternativeChip2: string | null;
    public __alternativechip2: string | null;

    public CANCELLED: boolean;
    public __cancelled: boolean;

    public CategoryID: number;
    public __categoryid: number;

    public COMPLETED: boolean;
    public __completed: boolean;

    public ContestantID: number;
    public __contestantid: number;

    public ContestantStatusID: number = 1;
    public __contestantstatusid: number = 1;

    public DNC: boolean;
    public __dnc: boolean;

    public DNF: boolean;
    public __dnf: boolean;

    public DNQ: boolean;
    public __dnq: boolean;

    public DNS: boolean;
    public __dns: boolean;

    public DSQ: boolean;
    public __dsq: boolean;

    public EventID: number;
    public __eventid: number;

    public ExitReason: string | null;
    public __exitreason: string | null;

    public FFChecked: boolean | null;
    public __ffchecked: boolean | null;

    public FlagTime: Date | null;
    public __flagtime: Date | null;

    public GroupID: number;
    public __groupid: number;

    public LAP: boolean;
    public __lap: boolean;

    public RaceID: number;
    public __raceid: number;

    public RankingCategories: RankingCategory[];
    public __rankingcategories: RankingCategory[];

    public REL: boolean;
    public __rel: boolean;

    public StartingTime: Date | null;
    public __startingtime: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AlternativeBib?: number | null;
        AlternativeChip1?: string | null;
        AlternativeChip2?: string | null;
        CANCELLED?: boolean;
        CategoryID?: number;
        COMPLETED?: boolean;
        ContestantID?: number;
        ContestantStatusID?: number;
        DNC?: boolean;
        DNF?: boolean;
        DNQ?: boolean;
        DNS?: boolean;
        DSQ?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        ExitReason?: string | null;
        FFChecked?: boolean | null;
        FlagTime?: Date | null;
        GroupID?: number;
        LAP?: boolean;
        RaceID?: number;
        RankingCategories?: RankingCategory[];
        REL?: boolean;
        StartingTime?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<RaceContestant>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('RaceContestant tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AlternativeBib = values.AlternativeBib ?? null;
        this.__alternativebib = values.AlternativeBib ?? null;
        Object.defineProperty(this, 'AlternativeBib', {
            get(): number | null {
                return this.__alternativebib;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AlternativeBib', this.__alternativebib, value);
                this.__alternativebib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AlternativeChip1 = values.AlternativeChip1 ?? null;
        this.__alternativechip1 = values.AlternativeChip1 ?? null;
        Object.defineProperty(this, 'AlternativeChip1', {
            get(): string | null {
                return this.__alternativechip1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('AlternativeChip1', this.__alternativechip1, value);
                this.__alternativechip1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AlternativeChip2 = values.AlternativeChip2 ?? null;
        this.__alternativechip2 = values.AlternativeChip2 ?? null;
        Object.defineProperty(this, 'AlternativeChip2', {
            get(): string | null {
                return this.__alternativechip2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('AlternativeChip2', this.__alternativechip2, value);
                this.__alternativechip2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CANCELLED = values.CANCELLED ?? false;
        this.__cancelled = values.CANCELLED ?? false;
        Object.defineProperty(this, 'CANCELLED', {
            get(): boolean {
                return this.__cancelled;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('CANCELLED', this.__cancelled, value);
                this.__cancelled = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryID = values.CategoryID ?? 0;
        this.__categoryid = values.CategoryID ?? 0;
        Object.defineProperty(this, 'CategoryID', {
            get(): number {
                return this.__categoryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('CategoryID', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.COMPLETED = values.COMPLETED ?? false;
        this.__completed = values.COMPLETED ?? false;
        Object.defineProperty(this, 'COMPLETED', {
            get(): boolean {
                return this.__completed;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('COMPLETED', this.__completed, value);
                this.__completed = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? 0;
        this.__contestantid = values.ContestantID ?? 0;
        Object.defineProperty(this, 'ContestantID', {
            get(): number {
                return this.__contestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantStatusID = values.ContestantStatusID ?? 1;
        this.__contestantstatusid = values.ContestantStatusID ?? 1;
        Object.defineProperty(this, 'ContestantStatusID', {
            get(): number {
                return this.__contestantstatusid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantStatusID', this.__contestantstatusid, value);
                this.__contestantstatusid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNC = values.DNC ?? false;
        this.__dnc = values.DNC ?? false;
        Object.defineProperty(this, 'DNC', {
            get(): boolean {
                return this.__dnc;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DNC', this.__dnc, value);
                this.__dnc = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNF = values.DNF ?? false;
        this.__dnf = values.DNF ?? false;
        Object.defineProperty(this, 'DNF', {
            get(): boolean {
                return this.__dnf;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DNF', this.__dnf, value);
                this.__dnf = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNQ = values.DNQ ?? false;
        this.__dnq = values.DNQ ?? false;
        Object.defineProperty(this, 'DNQ', {
            get(): boolean {
                return this.__dnq;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DNQ', this.__dnq, value);
                this.__dnq = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNS = values.DNS ?? false;
        this.__dns = values.DNS ?? false;
        Object.defineProperty(this, 'DNS', {
            get(): boolean {
                return this.__dns;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DNS', this.__dns, value);
                this.__dns = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DSQ = values.DSQ ?? false;
        this.__dsq = values.DSQ ?? false;
        Object.defineProperty(this, 'DSQ', {
            get(): boolean {
                return this.__dsq;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DSQ', this.__dsq, value);
                this.__dsq = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ExitReason = values.ExitReason ?? null;
        this.__exitreason = values.ExitReason ?? null;
        Object.defineProperty(this, 'ExitReason', {
            get(): string | null {
                return this.__exitreason;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ExitReason', this.__exitreason, value);
                this.__exitreason = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FFChecked = values.FFChecked ?? null;
        this.__ffchecked = values.FFChecked ?? null;
        Object.defineProperty(this, 'FFChecked', {
            get(): boolean | null {
                return this.__ffchecked;
            },
            set(value: boolean | null) {
                this.__entityStateHandler.propertyChanged('FFChecked', this.__ffchecked, value);
                this.__ffchecked = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FlagTime = values.FlagTime ?? null;
        this.__flagtime = values.FlagTime ?? null;
        Object.defineProperty(this, 'FlagTime', {
            get(): Date | null {
                return this.__flagtime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('FlagTime', this.__flagtime, value);
                this.__flagtime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GroupID = values.GroupID ?? 0;
        this.__groupid = values.GroupID ?? 0;
        Object.defineProperty(this, 'GroupID', {
            get(): number {
                return this.__groupid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GroupID', this.__groupid, value);
                this.__groupid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LAP = values.LAP ?? false;
        this.__lap = values.LAP ?? false;
        Object.defineProperty(this, 'LAP', {
            get(): boolean {
                return this.__lap;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('LAP', this.__lap, value);
                this.__lap = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RankingCategories = values.RankingCategories ?? [];
        this.__rankingcategories = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RankingCategories ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', newVal),
        });
        this.__rankingcategories.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', this.RankingCategories),
            );
        });
        Object.defineProperty(this, 'RankingCategories', {
            get(): RankingCategory[] {
                return this.__rankingcategories;
            },
            set(value: RankingCategory[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__rankingcategories = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: RankingCategory[], newVal: RankingCategory[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', newVal),
                });
                this.__rankingcategories.forEach((child: RankingCategory) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', this.RankingCategories),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', this.RankingCategories);
            },
            enumerable: true,
            configurable: true,
        });

        this.REL = values.REL ?? false;
        this.__rel = values.REL ?? false;
        Object.defineProperty(this, 'REL', {
            get(): boolean {
                return this.__rel;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('REL', this.__rel, value);
                this.__rel = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartingTime = values.StartingTime ?? null;
        this.__startingtime = values.StartingTime ?? null;
        Object.defineProperty(this, 'StartingTime', {
            get(): Date | null {
                return this.__startingtime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartingTime', this.__startingtime, value);
                this.__startingtime = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AlternativeBib": ${JSON.stringify(this.AlternativeBib)}` +
            ',' +
            `"AlternativeChip1": ${JSON.stringify(this.AlternativeChip1)}` +
            ',' +
            `"AlternativeChip2": ${JSON.stringify(this.AlternativeChip2)}` +
            ',' +
            `"CANCELLED": ${JSON.stringify(this.CANCELLED)}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"COMPLETED": ${JSON.stringify(this.COMPLETED)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"ContestantStatusID": ${JSON.stringify(this.ContestantStatusID)}` +
            ',' +
            `"DNC": ${JSON.stringify(this.DNC)}` +
            ',' +
            `"DNF": ${JSON.stringify(this.DNF)}` +
            ',' +
            `"DNQ": ${JSON.stringify(this.DNQ)}` +
            ',' +
            `"DNS": ${JSON.stringify(this.DNS)}` +
            ',' +
            `"DSQ": ${JSON.stringify(this.DSQ)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"ExitReason": ${JSON.stringify(this.ExitReason)}` +
            ',' +
            `"FFChecked": ${JSON.stringify(this.FFChecked)}` +
            ',' +
            `"FlagTime": ${this.FlagTime ? '"' + formatDateIso(this.FlagTime) + '"' : null}` +
            ',' +
            `"GroupID": ${JSON.stringify(this.GroupID)}` +
            ',' +
            `"LAP": ${JSON.stringify(this.LAP)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"RankingCategories": [ ${this.RankingCategories.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"REL": ${JSON.stringify(this.REL)}` +
            ',' +
            `"StartingTime": ${this.StartingTime ? '"' + formatDateIso(this.StartingTime) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of RaceContestant from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AlternativeBib?: number | null;
        AlternativeChip1?: string | null;
        AlternativeChip2?: string | null;
        CANCELLED?: boolean;
        CategoryID?: number;
        COMPLETED?: boolean;
        ContestantID?: number;
        ContestantStatusID?: number;
        DNC?: boolean;
        DNF?: boolean;
        DNQ?: boolean;
        DNS?: boolean;
        DSQ?: boolean;
        EventID?: number;
        ExitReason?: string | null;
        FFChecked?: boolean | null;
        FlagTime?: Date | null;
        GroupID?: number;
        LAP?: boolean;
        RaceID?: number;
        RankingCategories?: RankingCategory[];
        REL?: boolean;
        StartingTime?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): RaceContestant {
        return new RaceContestant(values);
    }

    public static _FromServerJson(json: any): RaceContestant {
        return new RaceContestant({
            ID: json.ID,
            AlternativeBib: json.AlternativeBib ?? null,
            AlternativeChip1: json.AlternativeChip1 ?? '',
            AlternativeChip2: json.AlternativeChip2 ?? '',
            CANCELLED: json.CANCELLED,
            CategoryID: json.CategoryID,
            COMPLETED: json.COMPLETED,
            ContestantID: json.ContestantID,
            ContestantStatusID: json.ContestantStatusID,
            DNC: json.DNC,
            DNF: json.DNF,
            DNQ: json.DNQ,
            DNS: json.DNS,
            DSQ: json.DSQ,
            EventID: json.EventID,
            ExitReason: json.ExitReason ?? '',
            FFChecked: json.FFChecked ?? null,
            FlagTime: json.FlagTime ? (json.FlagTime instanceof Date ? json.FlagTime : new Date(json.FlagTime)) : null,
            GroupID: json.GroupID,
            LAP: json.LAP,
            RaceID: json.RaceID,
            RankingCategories: json.RankingCategories?.map((x: any) => RankingCategory._FromServerJson(x)),
            REL: json.REL,
            StartingTime: json.StartingTime ? (json.StartingTime instanceof Date ? json.StartingTime : new Date(json.StartingTime)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of RaceContestant from another instance of RaceContestant.
     * @param other The RaceContestant you want to clone
     * @returns A cloned RaceContestant
     */
    public static FromOther(other: RaceContestant): RaceContestant {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return RaceContestant._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RaceContestant.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'AlternativeBib'
            | 'AlternativeChip1'
            | 'AlternativeChip2'
            | 'CANCELLED'
            | 'CategoryID'
            | 'COMPLETED'
            | 'ContestantID'
            | 'ContestantStatusID'
            | 'DNC'
            | 'DNF'
            | 'DNQ'
            | 'DNS'
            | 'DSQ'
            | 'EventID'
            | 'ExitReason'
            | 'FFChecked'
            | 'FlagTime'
            | 'GroupID'
            | 'LAP'
            | 'RaceID'
            | 'RankingCategories'
            | 'REL'
            | 'StartingTime'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'RaceContestant';
}
